import { ControlPointCheckinViewSchema, ControlPointViewSchema } from '@fsf/types';
import { _ } from '@ngx-translate/core';
import { z } from 'zod';

export type ControlPoint = z.infer<typeof ControlPointViewSchema>;

/**
 * A check-in history row: the Zod-validated ControlPointCheckinView plus a
 * flat `user_name` helper projected AFTER validation for the p-table column
 * (mirrors the defects-page responsible_user_name pattern). control_point_name
 * already lives on the view.
 */
export type ControlPointCheckinRow = z.infer<
  typeof ControlPointCheckinViewSchema
> & {
  user_name: string | null;
};

/** A qualification option as returned by GET /api/admin/qualifications. */
export interface QualificationOption {
  id: number;
  name: string;
}

export const StoreControlPointSchema = z.object({
  name: z.string().min(1, { message: 'validation.required' }).max(255),
  description: z.string().max(65535).nullable().optional(),
  interval_minutes: z
    .number({ message: 'validation.required' })
    .int()
    .min(1, { message: 'validation.required' })
    .max(480, { message: 'validation.maxLength' }),
  tag_name: z.string().min(1, { message: 'validation.required' }).max(255),
  qualification_ids: z.array(z.number()).optional(),
});

export type StoreControlPointPayload = z.infer<typeof StoreControlPointSchema>;

export const UpdateControlPointSchema = StoreControlPointSchema.partial();
export type UpdateControlPointPayload = z.infer<typeof UpdateControlPointSchema>;

export const controlPointTranslations = _([
  'controlPoints.controlPoints.title',
  'controlPoints.controlPoints.add',
  'controlPoints.controlPoints.edit',
  'controlPoints.controlPoints.empty',
  'controlPoints.controlPoints.emptyCta',
  'controlPoints.controlPoints.create',
  'controlPoints.controlPoints.deleteConfirmHeader',
  'controlPoints.controlPoints.deleteConfirmMessage',
  'controlPoints.controlPoints.created',
  'controlPoints.controlPoints.updated',
  'controlPoints.controlPoints.deleted',
  'controlPoints.controlPoints.notFound',
  'controlPoints.controlPoints.loadError',
  'controlPoints.controlPoints.createError',
  'controlPoints.controlPoints.updateError',
  'controlPoints.controlPoints.deleteError',
  'controlPoints.controlPoints.dashboardDescription',
  'controlPoints.controlPoints.dashboardButton',
  'controlPoints.controlPoints.qualificationCount',
  'controlPoints.controlPoints.fields.name',
  'controlPoints.controlPoints.fields.description',
  'controlPoints.controlPoints.fields.intervalMinutes',
  'controlPoints.controlPoints.fields.intervalMinutesHint',
  'controlPoints.controlPoints.fields.intervalMinutesRange',
  'controlPoints.controlPoints.fields.tagName',
  'controlPoints.controlPoints.fields.tagNameHint',
  'controlPoints.controlPoints.fields.qualifications',
  'controlPoints.controlPoints.fields.qualificationsPlaceholder',
  'controlPoints.controlPoints.fields.qualificationsHint',
]);

export const controlPointStatusTranslations = _([
  'controlPoints.status.title',
  'controlPoints.status.empty',
  'controlPoints.status.staleBanner',
  'controlPoints.status.configErrorTitle',
  'controlPoints.status.configErrorMessage',
  'controlPoints.status.lastCheck',
  'controlPoints.status.never',
  'controlPoints.status.pausedLabel',
  'controlPoints.status.states.ok',
  'controlPoints.status.states.due_soon',
  'controlPoints.status.states.overdue',
  'controlPoints.status.states.paused',
]);

export const controlPointHistoryTranslations = _([
  'controlPoints.history.title',
  'controlPoints.history.empty',
  'controlPoints.history.dashboardDescription',
  'controlPoints.history.dashboardButton',
  'controlPoints.history.columns.controlPoint',
  'controlPoints.history.columns.trainer',
  'controlPoints.history.columns.checkedInAt',
  'controlPoints.overview.title',
  'controlPoints.overview.dashboardDescription',
  'controlPoints.overview.dashboardButton',
]);
